import { useRef, useCallback } from 'react'
import { useAppDispatch } from '../store/hooks'
import { updateCartItem } from '../store/slices/cartSlice'

/**
 * Custom hook to manage cart operations with race condition prevention
 * Ensures only the latest operation for each product is processed
 */
export const useCartOperations = () => {
  const dispatch = useAppDispatch()
  
  // Track ongoing operations per product
  const ongoingOperations = useRef(new Map())

  const updateQuantity = useCallback(async (productId, quantity, type = 'update') => {
    console.log(`[useCartOperations] updateQuantity called:`, { productId, quantity, type })
    
    // Cancel any existing operation for this product
    if (ongoingOperations.current.has(productId)) {
      const existingOperation = ongoingOperations.current.get(productId)
      console.log(`[useCartOperations] Cancelling existing operation for product:`, productId)
      existingOperation.abort()
    }

    // Create new abort controller for this operation
    const controller = new AbortController()
    ongoingOperations.current.set(productId, controller)

    try {
      console.log(`[useCartOperations] Dispatching updateCartItem for:`, { productId, quantity, type })
      
      // Dispatch the update with abort signal
      const result = await dispatch(updateCartItem({
        productId,
        quantity,
        type,
        signal: controller.signal
      }))

      // Clean up on success
      ongoingOperations.current.delete(productId)
      console.log(`[useCartOperations] Operation completed successfully for:`, productId)
      return result
    } catch (error) {
      // Clean up on error (unless it was cancelled)
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        ongoingOperations.current.delete(productId)
        console.error(`[useCartOperations] Operation failed for ${productId}:`, error)
      } else {
        console.log(`[useCartOperations] Operation cancelled for:`, productId)
      }
      throw error
    }
  }, [dispatch])

  const removeItem = useCallback(async (productId) => {
    return updateQuantity(productId, 0, 'update')
  }, [updateQuantity])

  const addItem = useCallback(async (productId, quantity = 1, redirectToCart = false) => {
    // For add operations, we might want different behavior
    return updateQuantity(productId, quantity, 'add')
  }, [updateQuantity])

  // Cleanup function to cancel all ongoing operations
  const cancelAllOperations = useCallback(() => {
    ongoingOperations.current.forEach((controller) => {
      controller.abort()
    })
    ongoingOperations.current.clear()
  }, [])

  return {
    updateQuantity,
    removeItem,
    addItem,
    cancelAllOperations
  }
}